import { FC } from 'react';
import {
  AffinityTrendsDeltaContentSecondary,
  AffinityTrendsDeltaMovementText,
  AffinityTrendsDeltasGridContainer,
} from 'modules/intelligentInsights/brandAffinityInsights/components/affinityTrends/AffinityTrends.styles';
import { AffinityTrendsDeltaContent } from 'modules/intelligentInsights/brandAffinityInsights/components/affinityTrends/AffinityTrendsDeltaContent';
import {
  CalculatedDeltas,
  DeltaDirectionEnum,
  TrendTypeEnum,
} from 'modules/intelligentInsights/brandAffinityInsights/types';
import {
  getTrendProperties,
  formatDeltaTextContent,
} from 'modules/intelligentInsights/brandAffinityInsights/components/affinityTrends/AffinityTrendsDeltas.utils';
import {
  useBrandAffinityTranslation,
  BRAND_AFFINITY_NAMES_TO_I18N_KEYS,
} from 'modules/domains/brandAffinity';

export const AffinityTrendGainSummaryId = 'affinity-trend-gain-summary';
export const AffinityTrendDeclineSummaryId = 'affinity-trend-decline-summary';
export const AffinityTrendUnscoredSummaryId = 'affinity-trend-unscored-summary';

export interface AffinityTrendsDeltasProps {
  calculatedDeltas: CalculatedDeltas;
  trendType: TrendTypeEnum;
}
export const AffinityTrendsDeltas: FC<AffinityTrendsDeltasProps> = ({
  calculatedDeltas,
  trendType,
}) => {
  const { t } = useBrandAffinityTranslation();
  const { byCount, byPercent } = calculatedDeltas;
  const isPercent = trendType === TrendTypeEnum.Percent;
  const { gain, decline, unscored } = isPercent ? byPercent : byCount;
  const gainValue = isPercent ? gain.percent : gain.delta;
  const declineValue = isPercent ? decline.percent : decline.delta;
  const gainProperties = getTrendProperties(
    trendType,
    gain,
    DeltaDirectionEnum.gain,
    t(BRAND_AFFINITY_NAMES_TO_I18N_KEYS[gain.brandAffinityLabel]),
  );
  const declineProperties = getTrendProperties(
    trendType,
    decline,
    DeltaDirectionEnum.decline,
    t(BRAND_AFFINITY_NAMES_TO_I18N_KEYS[decline.brandAffinityLabel]),
  );
  const unscoredProperties = getTrendProperties(
    trendType,
    unscored,
    DeltaDirectionEnum.unscored,
    t(BRAND_AFFINITY_NAMES_TO_I18N_KEYS[unscored.brandAffinityLabel]),
  );

  return (
    <AffinityTrendsDeltasGridContainer>
      <AffinityTrendsDeltaContent
        data-test={AffinityTrendGainSummaryId}
        label={gain.brandAffinityLabel}
        titleText={gainProperties.title}
      >
        <AffinityTrendsDeltaMovementText>
          {gainValue > 0 ? (
            <>
              {formatDeltaTextContent(trendType, gainProperties.valueText)}
              <AffinityTrendsDeltaContentSecondary>
                {' '}
                {gainProperties.movementText}
              </AffinityTrendsDeltaContentSecondary>
            </>
          ) : (
            <AffinityTrendsDeltaContentSecondary>
              {' '}
              {gainProperties.dataThresholdText}
            </AffinityTrendsDeltaContentSecondary>
          )}
        </AffinityTrendsDeltaMovementText>
      </AffinityTrendsDeltaContent>
      <AffinityTrendsDeltaContent
        data-test={AffinityTrendDeclineSummaryId}
        label={decline.brandAffinityLabel}
        titleText={declineProperties.title}
      >
        <AffinityTrendsDeltaMovementText>
          {declineValue < 0 ? (
            <>
              {formatDeltaTextContent(trendType, declineProperties.valueText)}
              <AffinityTrendsDeltaContentSecondary>
                {' '}
                {declineProperties.movementText}
              </AffinityTrendsDeltaContentSecondary>
            </>
          ) : (
            <AffinityTrendsDeltaContentSecondary>
              {' '}
              {declineProperties.dataThresholdText}
            </AffinityTrendsDeltaContentSecondary>
          )}
        </AffinityTrendsDeltaMovementText>
      </AffinityTrendsDeltaContent>
      <AffinityTrendsDeltaContent
        data-test={AffinityTrendUnscoredSummaryId}
        displayUnscoredTip
        label={unscored.brandAffinityLabel}
        titleText={unscoredProperties.title}
      >
        <AffinityTrendsDeltaMovementText>
          {unscoredProperties.valueText}
          <AffinityTrendsDeltaContentSecondary>
            {' '}
            {unscoredProperties.movementText}
          </AffinityTrendsDeltaContentSecondary>
        </AffinityTrendsDeltaMovementText>
      </AffinityTrendsDeltaContent>
    </AffinityTrendsDeltasGridContainer>
  );
};
